/**
 * The storage tab's connection gate, restore analytics and reconnect refetch, shared by every
 * tab body (blob browser and queue messages — review Fable F7, review E E2) so a restored tab whose
 * connection was removed shows the same "connection missing" state, a restored tab counts once as
 * `restore`, and every tab recovers by itself whenever its connection generation is replaced
 * (a reconnect, or the explorer's in-place Reconnect — review F G1).
 */
import { useCallback, useEffect, useRef } from 'react';
import { hashKey, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { useStorageExplorerStore } from '../store/storageExplorerStore';
import type { StorageExplorerTabContent } from '../../core/types/workspaceTypes';
import { trackStorageBrowseOpen } from '../services/storageAnalytics';

/** The saved or active connection the tab points at; `undefined` once it was removed. */
export function useStorageTabConnection(connectionId: string) {
  return useConnectionStore(
    (s) => s.connections.find((c) => c.id === connectionId) ?? s.activeConnections.find((c) => c.id === connectionId)?.connection,
  );
}

/**
 * The tab's connection generation while it is connected — `ActiveConnection.generation`, stamped by
 * every successful connect, including the explorer's in-place Reconnect (`force: true`), which
 * replaces the entry without ever leaving `connected`. `undefined` while not connected.
 */
export function useStorageConnectionGeneration(connectionId: string): number | undefined {
  return useConnectionStore((s) => {
    const active = s.activeConnections.find((c) => c.id === connectionId && c.status === 'connected');
    return active ? active.generation ?? 0 : undefined;
  });
}

/**
 * Re-runs a tab's storage query whenever its connection generation is replaced (blob listing,
 * single-blob properties, queue peek — review E E2, review F G1): a not-connected → connected
 * transition AND an in-place Reconnect (connected → connected with a new generation). Main
 * released every cursor and queue receipt of the previous generation, and a query recreated while
 * disconnected could only answer NotConnected. Gated on `enabled` (a hidden, never-activated tab
 * does not fetch); `refetch` receives the new generation.
 */
export function useStorageReconnectRefetch(connectionId: string, enabled: boolean, refetch: (generation: number) => unknown): void {
  const generation = useStorageConnectionGeneration(connectionId);
  const seenGeneration = useRef(generation);
  useEffect(() => {
    const previous = seenGeneration.current;
    seenGeneration.current = generation;
    if (generation !== undefined && generation !== previous && enabled) void refetch(generation);
  }, [generation, enabled, refetch]);
}

const resetsThisCommit = new Set<string>();

/**
 * Runs a SHARED query's reconnect reset once per (query, generation) — review F G2. Tabs on the
 * same prefix share one listing query and every tab observes the connection-store update in the
 * same React commit; without this each observer's reconnect effect reset the query again, which
 * aborted the previous tab's fresh first-page fetch and listed twice. The token lives until the
 * commit's effects have run (a microtask). The fetch the first reset starts is tracked under that
 * tab (its query-options effect ran just before its reconnect effect), so a sibling's own cursor
 * release afterwards never cancels it.
 */
export function resetSharedStorageQueryOnce(sharedKey: string, generation: number, reset: () => void): void {
  const token = `${generation}|${sharedKey}`;
  if (resetsThisCommit.has(token)) return;
  resetsThisCommit.add(token);
  queueMicrotask(() => resetsThisCommit.delete(token));
  reset();
}

/**
 * The blob tab's reconnect (`useStorageListing`): a listing releases THIS tab's cursors (main
 * already dropped them with the old generation) and resets the listing query tabs share once per
 * generation — so Load more never sends a dead cursor; a single-blob attachment re-reads its
 * properties, likewise once per generation.
 */
export function useStorageListingReconnect(args: {
  tabId: string;
  connectionId: string;
  enabled: boolean;
  queryKey: QueryKey;
  singleBlob?: { resourceKey: string; refresh: () => void };
}): void {
  const { tabId, connectionId, enabled, queryKey, singleBlob } = args;
  const queryClient = useQueryClient();
  const singleBlobKey = singleBlob ? `properties|${connectionId}|${singleBlob.resourceKey}` : undefined;
  const singleBlobRefresh = singleBlob?.refresh;
  const onReconnect = useCallback((generation: number) => {
    if (singleBlobKey && singleBlobRefresh) {
      resetSharedStorageQueryOnce(singleBlobKey, generation, singleBlobRefresh);
      return;
    }
    useStorageExplorerStore.getState().resetListing(tabId);
    resetSharedStorageQueryOnce(hashKey(queryKey), generation, () => void queryClient.resetQueries({ queryKey, exact: true }));
  }, [queryClient, queryKey, singleBlobKey, singleBlobRefresh, tabId]);
  useStorageReconnectRefetch(connectionId, enabled, onReconnect);
}

/** Fires `feature:storage_browse_open { source: 'restore' }` once, on a restored tab's first activation. */
export function useStorageRestoreTracking(isActive: boolean, content: Pick<StorageExplorerTabContent, 'restoredFromSession' | 'mode'>): void {
  const restoreTrackedRef = useRef(false);
  useEffect(() => {
    if (!isActive || !content.restoredFromSession || restoreTrackedRef.current) return;
    restoreTrackedRef.current = true;
    trackStorageBrowseOpen(content.mode, 'restore');
  }, [isActive, content.restoredFromSession, content.mode]);
}
